import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ChevronUp, ChevronDown, Brain, PenTool, RefreshCcw, CheckCircle2, ImageIcon, Sparkles, Send, Loader2 } from "lucide-react";
import { updateDoc, doc, increment } from "firebase/firestore";

// 引用新架構
import { db } from "../config/firebase";
import { appId, SECURE_API_URL } from "../config/constants"; // 注意：這裡使用 SECURE_API_URL (Worker 2)
import { compressText, stripAnswerMarkersFromQuestion, normalizeAnswerText, normalizeConceptPair, sanitizeForFirestore } from "../utils/helpers";
import SmartNav from "../components/common/SmartNav";
import ProcessingModal from "../components/common/ProcessingModal";
import ExitConfirmModal from "../components/common/ExitConfirmModal";
import ResultFormatter from "../components/common/ResultFormatter";
import WatermarkOverlay from "../components/common/WatermarkOverlay";
import CopyrightFooter from "../components/common/CopyrightFooter";

const ResultPage = ({ user, userData, onCheckQuota, plansMap, sysConfig }) => {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state || !(state.simple || state.detailed)) return <Navigate to="/" replace />;

  const getHistoryTime = () => {
      if (!state.historyTimestamp) return "";
      const dateObj = state.historyTimestamp.seconds ? new Date(state.historyTimestamp.seconds * 1000) : new Date(state.historyTimestamp);
      return dateObj.toLocaleString([], { hour12: false });
  };

  const parseWorkerData = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return { top: "", bottom: "" };
      try {
        const j = JSON.parse(s);
        const text = (typeof j.text === "string" && j.text) || (typeof j.result === "string" && j.result) || "";
        if (text) {
          const parts = text.split("[DETAILED]");
          return { top: (parts[0] || "").trim(), bottom: (parts[1] || parts[0] || "").trim() };
        }
        if (j.top || j.bottom) return { top: String(j.top || "").trim(), bottom: String(j.bottom || "").trim() };
      } catch {}
      const parts = s.split("[DETAILED]");
      return { top: (parts[0] || "").trim(), bottom: (parts[1] || parts[0] || "").trim() };
  };

  const [activeView, setActiveView] = useState("analysis");
  const [resultTab, setResultTab] = useState("simple");
  const [displayResult, setDisplayResult] = useState({ simple: state.simple, detailed: state.detailed });
  
  const [quizData, setQuizData] = useState(null);
  const [quizExpanded, setQuizExpanded] = useState(false);
  const [quizInnerTab, setQuizInnerTab] = useState("answer");   
  const [quizCount, setQuizCount] = useState(0);
  const [showQuizAnswer, setShowQuizAnswer] = useState(false);
  
  const [keyConcepts, setKeyConcepts] = useState(null);
  const [conceptsTab, setConceptsTab] = useState("keypoints");
  const [conceptsCount, setConceptsCount] = useState(0);
  
  const [processingAction, setProcessingAction] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [supplementText, setSupplementText] = useState("");
  const [supplementLoading, setSupplementLoading] = useState(false);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  
  const [supplementCount, setSupplementCount] = useState(0);
  const [hasSavedQuiz, setHasSavedQuiz] = useState(false);
  const [hasSavedConcept, setHasSavedConcept] = useState(false);

  const updateUserStats = async (type) => {
      try {
        if (!userData || !user) return;
        const userRef = doc(db, "artifacts", appId, "public", "data", "registered_users", user.email.toLowerCase());
        if (type === "quiz") {
            await updateDoc(userRef, { todayQuizzes: increment(1), totalQuizzes: increment(1) });
        } else if (type === "concept") {
            await updateDoc(userRef, { todayConcepts: increment(1), totalConcepts: increment(1) });
        }
      } catch (err) {
          console.error("Stats update failed:", err);
      }
  };

  const handleAutoPreheat = async () => {
    try {
      setIsAutoLoading(true);
      if (!keyConcepts) {
        const r = await fetch(SECURE_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "concept",
            question: state.question,
            context: state.detailed,
            subjectType: state.subjectType || "math",
            preheat: true
          })
        });
        const rawData = await r.text();
        
        let k = "", d = "";
        try {
          const json = JSON.parse(rawData);
          let rawK = json.keypoints || json.summary;
          if (Array.isArray(rawK)) {
             rawK = rawK.map(s => typeof s === 'string' ? s.trim() : "").filter(s => s.length > 0 && s !== "--").join("\n");
          }
          let rawD = json.details || json.explanation || json.detailed || json.summaryDetails;
          if (Array.isArray(rawD)) {
             rawD = rawD.map(item => {
                if (typeof item === 'object' && item !== null) {
                    const title = item.title || "";
                    const body = item.body || item.content || "";
                    return `### ${title}\n${body}`;
                }
                return String(item);
             }).join("\n\n");
          }
          if (!rawK && !rawD && json.text) {
             rawK = "核心觀念解析";
             rawD = json.text;
          }
          k = rawK || "";
          d = rawD || k;
        } catch (jsonErr) {
           const parts = rawData.split("[DETAILED]");
           k = String(parts[0] || "").trim();
           d = String(parts[1] || parts[0] || "").trim();
        }

        if (k && k.length > 0 && !k.includes("Worker2 Failed")) {
          const fixed = normalizeConceptPair(k, d);
          setKeyConcepts({ keypoints: fixed.keypoints, details: fixed.details });
        }
      }

      await new Promise(resolve => setTimeout(resolve, 800));

      if (!quizData) {
        const r = await fetch(SECURE_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "quiz",
            question: state.question,
            inputData: { question: state.question },
            context: state.detailed,
            subjectType: state.subjectType || "math",
            preheat: true
          })
        });
        const rawData = await r.text();
        
        let q = "", a = "", d = "";
        try {
          const json = JSON.parse(rawData);
          if (json.variations && Array.isArray(json.variations) && json.variations.length > 0) {
            const v = json.variations[0];
            q = v.question || "";
            a = v.answer || "";
            d = v.solution || v.detailed || "";
          } else if (json.question) {
            q = json.question;
            a = json.answer;
            d = json.solution || json.detailed;
          }
        } catch (e) {}

        if (!q) {
             const parts = rawData.split("[DETAILED]");
             let rawTop = String(parts[0] || "").trim();
             const match = rawTop.match(/(\n\s*(\(|（)?(簡答|答案|Ans)(\)|）)?[:：].*$)/is);
             if (match) {
                 a = match[1].trim();
                 q = rawTop.replace(match[1], "").trim();
             } else {
                 q = rawTop;
                 a = "（請查看下方完整詳解）";
             }
             d = String(parts[1] || parts[0] || "").trim() || rawTop;
        }

        if (q && q.length > 0 && !q.includes("Worker2 Failed")) {
          setQuizData({
            question: stripAnswerMarkersFromQuestion(q),
            answer: normalizeAnswerText(a),
            detailed: compressText(d)
          });
        }
      }

    } catch (e) {
      console.warn("AutoPreheat warning:", e);
    } finally {
      setIsAutoLoading(false);
    }
  };

  const handleSimilarPractice = async () => {
    if (quizCount >= 3) return;
    try {
      setProcessingAction("quiz");
      const r = await fetch(SECURE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "quiz",
          question: state.question,
          inputData: { question: state.question },
          context: state.detailed,
          subjectType: state.subjectType || "math"
        })
      });

      const rawData = await r.text();
      let q = "", a = "", d = "";
      let isParsed = false;
      try {
        const json = JSON.parse(rawData);
        if (json.variations && Array.isArray(json.variations) && json.variations.length > 0) {
          const v = json.variations[0];
          q = v.question || "";
          a = v.answer || "";
          d = v.solution || v.detailed || ""; 
          isParsed = true;
        } else if (json.question) {
          q = json.question;
          a = json.answer;
          d = json.solution || json.detailed;
          isParsed = true;
        } else if (json.text && !json.error) {
           isParsed = false;
        }
      } catch (e) { }

      if (!isParsed) {
        let rawTop = rawData;
        try {
            const j = JSON.parse(rawData);
            if (j.text) rawTop = j.text;
        } catch(e){}

        const parts = rawTop.split("[DETAILED]");
        rawTop = String(parts[0] || "").trim();
        
        if (rawTop.includes("Worker2 Failed")) {
             alert("⚠️ 系統忙碌中，請稍後再試！");
             setProcessingAction(null);
             return;
        }

        const splitRegex = /(\n\s*(\(|（)?(簡答|答案|Ans)(\)|）)?[:：].*$)/is;
        const match = rawTop.match(splitRegex);
        if (match) {
            a = match[1].trim();
            q = rawTop.replace(match[1], "").trim();
        } else {
            if (rawTop.includes("(簡答)")) {
                const sp = rawTop.split("(簡答)");
                q = sp[0];
                a = "(簡答)" + sp.slice(1).join("");
            } else {
                q = rawTop;
                a = "（請查看下方完整詳解）";
            }
        }
        d = String(parts[1] || parts[0] || "").trim() || rawTop;
      }

      const newQuizData = {
        question: stripAnswerMarkersFromQuestion(compressText(q)) || "（題目生成中，請再試一次）",
        answer: normalizeAnswerText(compressText(a)) || "（請查看下方完整詳解）",
        detailed: compressText(d || "（AI 暫無回傳詳細內容，請再試一次）")
      };

      setQuizData(newQuizData);
      setQuizCount((prev) => prev + 1);
      setHasSavedQuiz(true);
      setActiveView("quiz");
      setQuizExpanded(false);
      setQuizInnerTab("answer");
      setShowQuizAnswer(false);

      if (state.logId && state.logId !== "temp_pending") {
        try {
           const safeQuizData = JSON.parse(JSON.stringify(newQuizData));
           await updateDoc(
            doc(db, "artifacts", appId, "public", "data", "question_logs", state.logId),
            { quizData: safeQuizData, quizCount: increment(1) }
          );
          await updateUserStats("quiz");
        } catch (dbErr) { console.error(dbErr); }
      }

    } catch (e) {
      console.error(e);
      alert("網路連線不穩，請檢查網路");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleKeyConcepts = async () => {
    if (conceptsCount >= 1) return;
    try {
      setProcessingAction("concept");
      const r = await fetch(SECURE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "concept",
          question: state.question,
          inputData: { question: state.question },
          context: state.detailed,
          subjectType: state.subjectType || "math"
        })
      });

      const rawData = await r.text();
      let k = "", d = "";
      let isParsed = false;
      try {
        const json = JSON.parse(rawData);
        let rawK = json.keypoints || json.summary;
        if (Array.isArray(rawK)) {
            rawK = rawK.map(s => typeof s === 'string' ? s.trim() : "").filter(s => s.length > 0 && s !== "--").join("\n");
        }
        let rawD = json.details || json.explanation || json.detailed || json.summaryDetails;
        if (Array.isArray(rawD)) {
            rawD = rawD.map(item => {
                if (typeof item === 'object' && item !== null) {
                    const title = item.title || "";
                    const body = item.body || item.content || "";
                    return `### ${title}\n${body}`;
                }
                return String(item);
            }).join("\n\n");
        }
        if (json.error || (json.text && json.text.includes("Worker2 Failed"))) console.warn("API Error:", json.error);
        if (!rawK && !rawD && json.text) {
            rawK = "核心觀念解析";
            rawD = json.text;
        }
        k = rawK || "";
        d = rawD || k;
        if (k || d) isParsed = true;
      } catch (e) { }

      if (!isParsed) {
         const parts = rawData.split("[DETAILED]");
         const top = String(parts[0] || "").trim();
         k = top;
         d = String(parts[1] || parts[0] || "").trim();
      }

      if (k.includes("Worker2 Failed")) {
          alert("⚠️ 系統忙碌中，請稍後再試！");
          setProcessingAction(null);
          return;
      }

      const fixed = normalizeConceptPair(k, d);
      const newConceptData = { keypoints: fixed.keypoints, details: fixed.details };
      setKeyConcepts(newConceptData);
      setConceptsCount(1);
      setHasSavedConcept(true);
      setActiveView("concept");

      if (state.logId && state.logId !== "temp_pending") {
        try {
          const safeConceptData = JSON.parse(JSON.stringify(newConceptData));
          await updateDoc(
            doc(db, "artifacts", appId, "public", "data", "question_logs", state.logId),
            { conceptData: safeConceptData, hasUsedConcept: true }
          );
          await updateUserStats("concept");
        } catch (dbErr) { console.error(dbErr); }
      }

    } catch (e) {
      console.error(e);
      alert("整理失敗，請稍後再試");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleSupplementSubmit = async () => {
    if (!supplementText.trim() || supplementLoading) return;
    if (supplementCount >= 3) {
        alert("🚫 本題追問次數已達上限 (3次)，請嘗試自行思考或開始新的一題！");
        return;
    }

    setSupplementLoading(true);
    try {
      const r = await fetch(SECURE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "verify",
          inputData: { question: state.question, userAnswer: supplementText },
          context: displayResult.detailed,
          subjectType: state.subjectType || "math"
        })
      });

      const rawData = await r.text();
      const { top, bottom } = parseWorkerData(rawData);
      
      setDisplayResult({ 
        simple: top || "分析結果", 
        detailed: bottom || top 
      });
      setSupplementText("");
      setSupplementCount(prev => prev + 1);
      setActiveView("analysis");
      setResultTab("simple");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error(e);
      alert("連線失敗，請檢查網路");
    } finally {
      setSupplementLoading(false);
    }
  };

  const handleSmartNav = async (tab) => {
    if (tab === "upload") {
      setShowExitConfirm(true);
      return;
    }
    if (tab === "analysis") {
      setActiveView("analysis");
      return;
    }
    if (tab === "quiz") {
      if (hasSavedQuiz && quizData) {
        setActiveView("quiz");
        return;
      }
      onCheckQuota("quiz", async () => {
        if (quizData) {
          setHasSavedQuiz(true);
          setActiveView("quiz");
          setQuizExpanded(false);
          setQuizInnerTab("answer");
          if (quizCount === 0) setQuizCount(1);
          if (state.logId && state.logId !== "temp_pending") {
            (async () => {
              try {
                const safePayload = sanitizeForFirestore({ quizData, quizCount: 1 });
                await updateDoc(doc(db, "artifacts", appId, "public", "data", "question_logs", state.logId), safePayload);
                await updateUserStats("quiz");
              } catch (e) { console.warn("Background save failed:", e); }
            })();
          }
        } else {
          await handleSimilarPractice();
        }
      });
      return;
    }
    if (tab === "concept") {
      if (hasSavedConcept && keyConcepts) {
        setActiveView("concept");
        return;
      }
      onCheckQuota("concept", async () => {
        if (keyConcepts) {
          setHasSavedConcept(true);
          setActiveView("concept");
          if (conceptsCount === 0) setConceptsCount(1);
          if (state.logId && state.logId !== "temp_pending") {
            (async () => {
              try {
                const safePayload = sanitizeForFirestore({ conceptData: keyConcepts, hasUsedConcept: true });
                await updateDoc(doc(db, "artifacts", appId, "public", "data", "question_logs", state.logId), safePayload);
                await updateUserStats("concept");
              } catch (e) { console.warn("Background save failed:", e); }
            })();
          }
        } else {
          await handleKeyConcepts();
        }
      });
      return;
    }
  };

  useEffect(() => {
    if (state?.question && sysConfig?.enableAutoPreheat === true) {
      handleAutoPreheat();
    }
  }, [state?.question, sysConfig?.enableAutoPreheat]);

  return (
    <div className="p-4 md:p-8 flex-1 flex flex-col gap-6 fade-in pb-16 relative">
          {processingAction && <ProcessingModal type={processingAction} />}
          {showExitConfirm && <ExitConfirmModal onConfirm={() => navigate("/")} onCancel={() => setShowExitConfirm(false)} />}
          
          <div className="flex flex-col gap-2 relative z-20">
              <button onClick={() => setShowExitConfirm(true)} className="flex items-center gap-2 text-slate-500 font-bold hover:text-orange-500 transition-colors w-fit"><ArrowLeft size={20} /> 回首頁</button>
              {state.historyTimestamp && (
                  <div className="bg-slate-100 border border-slate-200 p-3 rounded-xl flex items-center gap-3 text-slate-600 animate-in fade-in slide-in-from-top-2">
                      <div className="bg-slate-200 p-2 rounded-full"><Clock size={16}/></div>
                      <div className="flex-1"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">歷史回顧模式</p><p className="text-sm font-black text-slate-700">解題時間：{getHistoryTime()}</p></div>
                  </div>
              )}
          </div>
    
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative z-20">
            <button onClick={() => setShowOriginal(!showOriginal)} className="w-full px-4 py-3 flex justify-between bg-slate-50 text-xs font-bold text-slate-500"><span>👁️ 查看原始題目與圖片</span>{showOriginal ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
            {showOriginal && (
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                    {(state.imageBase64 || state.thumbnail) && <img src={state.imageBase64 || state.thumbnail} alt="original" className="max-h-48 rounded-lg mb-3 mx-auto shadow-sm" />}
                    <p className="text-slate-600 text-sm whitespace-pre-wrap">{state.question}</p>
                </div>
            )}
          </div>
    
          <SmartNav onNav={handleSmartNav} activeTab={activeView} quizCount={quizCount} conceptCount={conceptsCount} userData={userData} plansMap={plansMap} sysConfig={sysConfig} />

          {activeView === "analysis" && (
            <div className="bg-white rounded-[2rem] border border-orange-100 shadow-xl overflow-hidden fade-in relative ring-4 ring-orange-50/50">
              <div className="p-6 bg-gradient-to-r from-orange-500 to-amber-500 flex items-center gap-3 text-white font-black text-xl shadow-md">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm"><Brain size={24} className="text-white" /></div> 
                AI 智能解析結果
              </div>
              <div className="p-6 relative min-h-[400px]">
                <WatermarkOverlay userData={userData} />
                <div className="flex p-1 bg-slate-100 rounded-full mb-8 max-w-xs mx-auto border border-slate-200 relative z-20 shadow-inner">
                  <button onClick={() => setResultTab("simple")} className={`flex-1 py-2 text-sm font-bold rounded-full transition-all ${resultTab === "simple" ? "bg-white text-orange-600 shadow-sm scale-105" : "text-slate-400 hover:text-slate-600"}`}>簡易解答</button>
                  <button onClick={() => setResultTab("detailed")} className={`flex-1 py-2 text-sm font-bold rounded-full transition-all ${resultTab === "detailed" ? "bg-white text-orange-600 shadow-sm scale-105" : "text-slate-400 hover:text-slate-600"}`}>完整詳解</button>
                </div>
                <div className="relative z-20">
                  <ResultFormatter variant={resultTab} text={resultTab === "simple" ? displayResult.simple : displayResult.detailed} />
                </div>
              </div>
            </div>
          )}
    
          {activeView === "quiz" && quizData && (
            <div className="bg-white rounded-[2rem] border border-blue-100 shadow-xl overflow-hidden fade-in relative">
              <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-500 flex items-center justify-between shadow-md text-white">
                <div className="flex items-center gap-3 font-black text-xl"><PenTool size={24} /> 舉一反三</div>
                <button onClick={() => onCheckQuota("quiz", () => handleSimilarPractice())} className="bg-white/20 border border-white/30 px-3 py-1.5 rounded-full text-xs text-white font-bold flex items-center gap-1 hover:bg-white/30 transition-all" disabled={quizCount >= 3}><RefreshCcw size={14} /> 再出一題 ({3 - quizCount})</button>
              </div>
              <div className="p-6 relative min-h-[400px] z-10">
                <WatermarkOverlay userData={userData} />
                <div className="relative z-20">
                  <div className="flex items-center gap-2 mb-4 opacity-60">
                      <div className="w-1 h-4 bg-slate-400 rounded-full"></div>
                      <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Question / 題目</span>
                      <div className="h-px bg-slate-200 flex-1"></div>
                  </div>
                  <div className="p-6 rounded-[1.5rem] border border-blue-100 bg-slate-50/50 shadow-inner">
                    <ResultFormatter text={quizData.question} />
                  </div>
                </div>

                <div className="relative z-20 mt-8">
                  <button onClick={() => setQuizExpanded(v => !v)} className="w-full py-3.5 rounded-2xl font-bold text-sm shadow-sm border border-blue-200 bg-white text-blue-600 hover:bg-blue-50 active:scale-[0.99] transition-all flex items-center justify-center gap-2">
                    {quizExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    {quizExpanded ? "收起解析" : "查看答案與詳解"}
                  </button>
                </div>

                {quizExpanded && (
                  <div className="relative z-20 mt-6 animate-in slide-in-from-top-4 fade-in duration-300">
                    <div className="flex p-1 bg-slate-100 rounded-full mb-8 border border-slate-200 max-w-sm mx-auto w-full shadow-inner">
                      <button onClick={() => setQuizInnerTab("answer")} className={`flex-1 py-3 text-sm font-black rounded-full transition-all ${quizInnerTab === "answer" ? "bg-white text-blue-700 shadow-md" : "text-slate-500 hover:text-slate-700"}`}>標準答案</button>
                      <button onClick={() => setQuizInnerTab("detailed")} className={`flex-1 py-3 text-sm font-black rounded-full transition-all ${quizInnerTab === "detailed" ? "bg-white text-blue-700 shadow-md" : "text-slate-500 hover:text-slate-700"}`}>完整詳解</button>
                    </div>                                                                       
                    {quizInnerTab === "answer" ? (
                      <div className="content-layer p-6 bg-white rounded-[1.5rem] border border-blue-100 shadow-sm relative overflow-hidden">
                        <div className="flex items-center gap-2 mb-4 opacity-60">
                            <span className="text-xs font-black text-blue-900 uppercase tracking-widest flex items-center gap-1"><CheckCircle2 size={14}/> Answer</span>
                            <div className="h-px bg-blue-100 flex-1"></div>
                        </div>
                        {!showQuizAnswer ? (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                                <p className="text-slate-400 font-bold mb-3 text-xs tracking-wider">思考過了嗎？</p>
                                <button onClick={() => setShowQuizAnswer(true)} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all text-xs flex items-center gap-2">
                                    <ImageIcon size={14}/> 顯示答案
                                </button>
                            </div>
                        ) : null}
                        <div className={!showQuizAnswer ? "blur-md opacity-30 select-none" : ""}>
                             <ResultFormatter variant="detailed" text={quizData.answer || "（請切到「完整詳解」查看）"} />
                        </div>
                      </div>
                    ) : (
                      <div className="content-layer p-6 bg-white rounded-[1.5rem] border border-blue-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-4 opacity-60">
                            <span className="text-xs font-black text-blue-900 uppercase tracking-widest">Detailed Solution</span>
                            <div className="h-px bg-blue-100 flex-1"></div>
                        </div>
                        <ResultFormatter text={quizData.detailed} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
    
          {activeView === "concept" && keyConcepts && (
            <div className="bg-white rounded-[2rem] border border-yellow-200 shadow-xl overflow-hidden fade-in relative">
              <div className="p-6 bg-gradient-to-r from-yellow-500 to-amber-500 flex items-center gap-3 text-white font-black text-xl shadow-md">
                <Sparkles size={24} /> ✨ 重點整理
              </div>
              <div className="p-6 relative min-h-[400px] z-10">
                <WatermarkOverlay userData={userData} />
                <div className="flex p-1 bg-yellow-50 rounded-full mb-8 max-w-xs mx-auto border border-yellow-200 relative z-20">
                  <button onClick={() => setConceptsTab("keypoints")} className={`flex-1 py-2 text-sm font-bold rounded-full ${conceptsTab === "keypoints" ? "bg-white text-amber-600 shadow-sm" : "text-amber-400"}`}>核心重點</button>
                  <button onClick={() => setConceptsTab("details")} className={`flex-1 py-2 text-sm font-bold rounded-full ${conceptsTab === "details" ? "bg-white text-amber-600 shadow-sm" : "text-amber-400"}`}>詳細說明</button>
                </div>
                <div className="relative z-20">
                  <ResultFormatter text={conceptsTab === "keypoints" ? (keyConcepts?.keypoints ?? "") : (keyConcepts?.details ?? "")} />
                </div>
              </div>
            </div>
          )}
    
          <div className="mt-8 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative z-20 flex gap-2">
            <input type="text" value={supplementText} onChange={(e) => setSupplementText(e.target.value)} placeholder={supplementCount >= 3 ? "本題追問次數已用完" : `針對此題哪裡還不懂？(剩餘 ${3 - supplementCount} 次)`} disabled={supplementCount >= 3 || supplementLoading} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 disabled:opacity-50 disabled:bg-slate-100" />
            <button onClick={handleSupplementSubmit} disabled={supplementLoading || !supplementText || supplementCount >= 3} className="bg-slate-800 text-white p-3 rounded-xl flex items-center justify-center min-w-[50px] disabled:opacity-50">
                {supplementLoading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            </button>
          </div>
          <CopyrightFooter />
    </div>
  );
}

export default ResultPage;
